import React, { useState, useEffect, useMemo, useCallback, lazy, Suspense } from 'react';
import { Routes, Route, Navigate, useLocation, useNavigate } from 'react-router-dom';
import './App.css';
import './styles/Modal.css';
import './styles/ButtonStyles.css';
import Calculator from './components/Calculator';
import Auth from './components/Auth';
import HomePage from './components/HomePage';
import ShopPage from './pages/ShopPage';
import PrivacyPolicyPage from './pages/PrivacyPolicyPage'; 
import TermsOfServicePage from './pages/TermsOfServicePage'; 
import AboutUsPage from './pages/AboutUsPage';
import AccountPage from './pages/AccountPage';
import ScrollToTop from './components/ScrollToTop';
import { useAuth } from './contexts/AuthContext';
import { calculateSectionCompletionStatus } from './utils/sectionCompletionUtils';
import DashboardLayout from './components/layout/DashboardLayout';
import ErrorBoundary from './components/ErrorBoundary';
import FeedbackButton from './components/feedback/FeedbackButton';
import { gaPageView } from './utils/analytics';

// Lazy-loaded admin pages — not included in the main bundle
const AdminFeedbackPage = lazy(() => import('./pages/AdminFeedbackPage'));
const AdminUserStatsPage = lazy(() => import('./pages/AdminUserStatsPage'));
const AdminActivityPage = lazy(() => import('./pages/AdminActivityPage'));
const AdminGlobalSettingsPage = lazy(() => import('./pages/AdminGlobalSettingsPage'));
const AdminDeletionRequestsPage = lazy(() => import('./pages/AdminDeletionRequestsPage'));
const AdminRecommendedProductsPage = lazy(() => import('./pages/AdminRecommendedProductsPage'));
const ColorMixPage = lazy(() => import('./pages/ColorMixPage'));

// Protected route component - redirects to login with ?redirect param so user returns after auth
const ProtectedRoute = ({ children, redirectTo }) => {
  const { user, loading } = useAuth();
  const location = useLocation();
  
  if (loading) {
    return <div className="loading-screen">Loading...</div>;
  }
  
  if (!user) {
    const destination = redirectTo || location.pathname;
    return <Navigate to={`/login?redirect=${destination}`} />;
  }
  
  return children;
};

// Admin-only route - requires login AND is_admin flag on profile
const AdminRoute = ({ children }) => {
  const { user, loading, proAccessLoading, profile } = useAuth();
  const location = useLocation();
  
  if (loading || proAccessLoading) {
    return <div className="loading-screen">Loading...</div>;
  }
  
  if (!user) {
    return <Navigate to={`/login?redirect=${location.pathname}`} />;
  }
  
  if (!profile?.is_admin) {
    return <Navigate to="/" />;
  }
  
  return children;
};

// NOTE: The Pro Calculator is now open to everyone (anonymous included).
// Save/Export actions are gated by a friendly login modal inside the calculator,
// so the old ProRoute gate was removed.

// ProjectManagerModal removed - now using inline section in ProCalculator

function AppContent() {
  const { user, loading } = useAuth();
  const location = useLocation(); // Get location object
  const navigate = useNavigate(); // Get navigate function
  
  // Initialize dark mode state
  const [isDarkMode, setIsDarkMode] = useState(() => {
    // Check localStorage for saved preference, default to true (dark)
    const savedMode = localStorage.getItem('darkMode');
    return savedMode ? JSON.parse(savedMode) : true;
  });

  // Effect to apply the dark mode class to the body and save preference
  useEffect(() => {
    if (isDarkMode) {
      document.body.classList.add('dark-mode');
    } else {
      document.body.classList.remove('dark-mode');
    }
    // Save the user's preference to localStorage
    localStorage.setItem('darkMode', JSON.stringify(isDarkMode));
  }, [isDarkMode]); // Re-run only when isDarkMode changes
  
  // Track calculator version
  const [calculatorVersion, setCalculatorVersion] = useState(() => {
    return localStorage.getItem('calculatorVersion') || 'simple';
  });
  
  // Unit states - now handled in ProCalculatorContext
  
  // Active section state for Pro Calculator
  const [activeSection, setActiveSection] = useState('reference');
  
  // State persistence for calculator inputs - MUST be declared before callbacks that use them
  const [basicCalculatorState, setBasicCalculatorState] = useState({});
  const [proCalculatorState, setProCalculatorState] = useState({}); // Initialize Pro state
  
  // Manually track section completion status for Pro Calculator
  const [sectionCompletionStatus, setSectionCompletionStatus] = useState({});
  
  // Handler for loading a project is now handled in ProCalculator

  // Project action handler removed

  // Load calculator states from localStorage on initial mount
  useEffect(() => {
    const savedProState = localStorage.getItem('proCalculatorState');
    
    if (savedProState && savedProState !== "undefined") { // Check for null AND the literal string "undefined"
      try {
        const parsedProState = JSON.parse(savedProState);
        // Ensure parsedProState is a valid object before setting
        if (parsedProState && typeof parsedProState === 'object') {
          setProCalculatorState(parsedProState);
        } else {
          localStorage.removeItem('proCalculatorState'); // Remove corrupted item
          setProCalculatorState({}); // Reset to default
        }
      } catch (error) {
        localStorage.removeItem('proCalculatorState'); // Remove corrupted item on other parse errors too
        setProCalculatorState({}); // Reset to default
      }
    } else if (savedProState === "undefined") {
      // Handle the specific case where the localStorage item IS the string "undefined"
      localStorage.removeItem('proCalculatorState');
      setProCalculatorState({}); // Reset to default empty state
    }
    // If savedProState is null (no item found), proCalculatorState remains its initial {} (empty object), which is fine.

    // Assuming basicCalculatorState is also persisted and uses 'basicCalculatorState' key
    const savedBasicState = localStorage.getItem('basicCalculatorState'); 
    if (savedBasicState) {
      try {
        const parsedBasicState = JSON.parse(savedBasicState);
        setBasicCalculatorState(parsedBasicState);
      } catch (error) {
        // localStorage.removeItem('basicCalculatorState'); // Optional: clear corrupted item
      }
    }
  }, []); // Empty dependency array: runs only once on mount

  // Callback for ProCalculator to update its state in App.js
  const handleProCalculatorStateChange = useCallback((newState) => {
    setProCalculatorState(newState); 
    localStorage.setItem('proCalculatorState', JSON.stringify(newState));

    // Directly calculate and set section status with the state from context
    const newStatusFromContext = calculateSectionCompletionStatus(newState || {});
    
    // Compare with current App-level sectionCompletionStatus before setting
    // to avoid unnecessary re-renders if the status hasn't changed.
    const currentAppStatusString = JSON.stringify(sectionCompletionStatus);
    const newStatusFromContextString = JSON.stringify(newStatusFromContext);

    if (currentAppStatusString !== newStatusFromContextString) {
      setSectionCompletionStatus(newStatusFromContext);
    } else {
    }

  }, [setProCalculatorState, sectionCompletionStatus]); // MODIFIED: Added sectionCompletionStatus to dependency array

  const handleBasicCalculatorStateChange = useCallback((newState) => {
    setBasicCalculatorState(newState); // Update the state holder
  }, []);

  // Calculate section completion status based on the proCalculatorState
  const calculatedSectionCompletionStatus = useMemo(() => {
    const newStatus = calculateSectionCompletionStatus(proCalculatorState || {});
    return newStatus;
  }, [proCalculatorState]);

  // Update the state variable only when the calculated value actually changes
  useEffect(() => {
    const currentStatusString = JSON.stringify(sectionCompletionStatus);
    const newStatusString = JSON.stringify(calculatedSectionCompletionStatus);
    if (currentStatusString !== newStatusString) {
      setSectionCompletionStatus(calculatedSectionCompletionStatus);
    } else {
    }
  // eslint-disable-next-line react-hooks/exhaustive-deps -- 3.4.2026
  }, [calculatedSectionCompletionStatus]); // MODIFIED DEPENDENCY ARRAY

  // Update calculator version when localStorage changes
  useEffect(() => {
    localStorage.setItem('calculatorVersion', calculatorVersion);
  }, [calculatorVersion]);
  
  // Initialize section completion status when the component mounts
  useEffect(() => {
    // Set initial status for testing - we'll set reference to incomplete
    // updateSectionStatus('reference', 'incomplete');
    // updateSectionStatus('cookieNames', 'neutral');
    // updateSectionStatus('dimensions', 'neutral');
    // updateSectionStatus('sets', 'neutral');
    // updateSectionStatus('colors', 'neutral');
    // updateSectionStatus('orders', 'neutral');
    // updateSectionStatus('results', 'neutral');
    // updateSectionStatus('all', 'neutral');
  }, []);

  // Handle Google OAuth redirect - after OAuth callback, navigate to intended destination
  useEffect(() => {
    if (user && !loading) {
      const googleAuthInProgress = localStorage.getItem('googleAuthInProgress');
      const authRedirectUrl = localStorage.getItem('authRedirectUrl');
      
      if (googleAuthInProgress === 'true' && authRedirectUrl) {
        localStorage.removeItem('googleAuthInProgress');
        localStorage.removeItem('authRedirectUrl');
        
        // Extract just the path from the stored URL
        try {
          const url = new URL(authRedirectUrl);
          const path = url.pathname;
          if (path && path !== '/' && location.pathname !== path) {
            navigate(path);
          }
        } catch (e) {
          // If URL parsing fails, ignore
        }
      }
    }
  }, [user, loading]); // eslint-disable-line react-hooks/exhaustive-deps

  // Fire GA4 page_view on every SPA route change
  useEffect(() => {
    gaPageView(location.pathname + location.search);
  }, [location.pathname, location.search]);

  // Effect to synchronize calculatorVersion with the current route
  useEffect(() => {
    // Check for Pro Calculator route
    if (location.pathname === '/pro') { // Correct path for Pro
      if (calculatorVersion !== 'pro') {
        setCalculatorVersion('pro');
      }
    // Check for Simple Calculator routes (adjust paths if needed)
    } else if (location.pathname === '/calculator' || location.pathname === '/') { 
      if (calculatorVersion !== 'simple') {
        setCalculatorVersion('simple');
      }
    }
    // Not touching calculatorVersion if on other pages like /account, /shop etc.
  }, [location.pathname, calculatorVersion, setCalculatorVersion]);

  // Show loading indicator while checking auth status
  if (loading) {
    return (
      <div className="App">
        <div className="loading-screen">Loading authentication...</div>
      </div>
    );
  }

  // Show a clean screen during Google OAuth redirect to avoid home page flash
  if (typeof window !== 'undefined' && localStorage.getItem('googleAuthInProgress') === 'true') {
    return (
      <div className="App">
        <div className="loading-screen">Completing sign-in…</div>
      </div>
    );
  }
  
  // Pro calculator state is now handled in ProCalculatorContext
  
  // Keep in sync with SKIP_SETS in ProCalculatorContext.js
  const SKIP_SETS = true;

  // Define sections for the Pro Calculator
  const sections = [
    { id: 'reference', label: 'Get Started' },
    { id: 'cookieNames', label: 'Cookie Names' },
    ...(!SKIP_SETS ? [{ id: 'sets', label: 'Sets' }] : []),
    { id: 'dimensions', label: 'Size & Shape' },
    { id: 'colors', label: 'Colors' },
    { id: 'orders', label: 'How Many' },
    { id: 'results', label: 'My Icing Plan' },
    { id: 'icingPlans', label: 'Icing Plans' }
  ];
  
  return (
    <div className={`App ${isDarkMode ? 'dark-mode' : ''}`}>
      <ScrollToTop />
      <FeedbackButton /> {/* Added FeedbackButton */}
      <Routes>
        <Route path="/login" element={!user ? <Auth initialTab={new URLSearchParams(window.location.search).get('tab') === 'signup' ? 'signup' : 'login'} /> : <Navigate to={new URLSearchParams(window.location.search).get('redirect') || '/'} />} />
        
        {/* Color Mix AI - open to everyone; sign-in prompt shown inside the page for anonymous users */}
        <Route path="/color-mix" element={
          <Suspense fallback={<div className="loading-screen">Loading...</div>}>
            <DashboardLayout
              isProCalculator={false}
              sections={sections}
              activeSection={null}
              setActiveSection={() => {}}
              isDarkMode={isDarkMode}
              setIsDarkMode={setIsDarkMode}
              calculatorVersion={calculatorVersion}
              setCalculatorVersion={setCalculatorVersion}
              sectionCompletionStatus={sectionCompletionStatus}
            >
              <ColorMixPage />
            </DashboardLayout>
          </Suspense>
        } />
        
        {/* Shop Page */}
        <Route path="/shop" element={
          <DashboardLayout
            isProCalculator={false} // Shop page is not part of the pro calculator flow
            sections={sections} // Pass sections for sidebar consistency if needed, or filter them
            activeSection={null} // No specific pro calc section is active
            setActiveSection={() => {}} // No pro calc section to set
            isDarkMode={isDarkMode}
            setIsDarkMode={setIsDarkMode}
            calculatorVersion={calculatorVersion} // Or a general version if preferred
            setCalculatorVersion={setCalculatorVersion}
            sectionCompletionStatus={sectionCompletionStatus}
          >
            <ShopPage />
          </DashboardLayout>
        } />
        
        {/* Home page at root */}
        <Route path="/" element={
          <DashboardLayout 
            isProCalculator={false}
            sections={sections}
            activeSection={activeSection}
            setActiveSection={setActiveSection}
            isDarkMode={isDarkMode}
            setIsDarkMode={setIsDarkMode}
            calculatorVersion="simple"
            setCalculatorVersion={setCalculatorVersion}
            sectionCompletionStatus={sectionCompletionStatus}
          >
            <HomePage />
          </DashboardLayout>
        } />
        
        {/* Basic Calculator retired — redirect old bookmarks/links to Home */}
        <Route path="/basic" element={<Navigate to="/" replace />} />
        
        {/* Pro Calculator - open to everyone; Save/Export gated by a login modal */}
        <Route path="/pro" element={
          <ErrorBoundary>
            <DashboardLayout 
                isProCalculator={true}
                sections={sections}
                activeSection={activeSection}
                setActiveSection={setActiveSection}
                isDarkMode={isDarkMode}
                setIsDarkMode={setIsDarkMode}
                calculatorVersion="pro"
                setCalculatorVersion={setCalculatorVersion}
                sectionCompletionStatus={sectionCompletionStatus}
              >
                <Calculator 
                  isPro={true} 
                  isDarkMode={isDarkMode} 
                  setIsDarkMode={setIsDarkMode}
                  calculatorVersion="pro"
                  setCalculatorVersion={setCalculatorVersion}
                  activeSection={activeSection}
                  setActiveSection={setActiveSection}
                  savedState={proCalculatorState} 
                  onStateChange={handleProCalculatorStateChange} 
                />
              </DashboardLayout>
          </ErrorBoundary>
        } />
        
        {/* Account Page Route - Protected */}
        <Route 
          path="/account" 
          element={
            <ProtectedRoute>
              <DashboardLayout
                isProCalculator={false} // Or determine based on context if needed
                sections={sections} // Pass sections for sidebar consistency
                activeSection={null} // No pro calc section active
                setActiveSection={() => {}} // No pro calc section to set
                isDarkMode={isDarkMode}
                setIsDarkMode={setIsDarkMode}
                calculatorVersion={calculatorVersion}
                setCalculatorVersion={setCalculatorVersion}
                sectionCompletionStatus={sectionCompletionStatus}
                noWrapperStyling={true}
              >
                <AccountPage />
              </DashboardLayout>
            </ProtectedRoute>
          }
        />
        
        {/* Admin pages — lazy loaded, only downloaded by admins */}
        <Route path="/admin/userstats" element={
          <AdminRoute>
            <Suspense fallback={<div className="loading-screen">Loading...</div>}>
              <DashboardLayout
                isProCalculator={false}
                sections={sections}
                activeSection={null}
                setActiveSection={() => {}}
                isDarkMode={isDarkMode}
                setIsDarkMode={setIsDarkMode}
                calculatorVersion={calculatorVersion}
                setCalculatorVersion={setCalculatorVersion}
                sectionCompletionStatus={sectionCompletionStatus}
                noWrapperStyling={true}
              >
                <AdminUserStatsPage />
              </DashboardLayout>
            </Suspense>
          </AdminRoute>
        } />

        <Route path="/admin/feedback" element={
          <AdminRoute>
            <Suspense fallback={<div className="loading-screen">Loading...</div>}>
              <DashboardLayout
                isProCalculator={false}
                sections={sections}
                activeSection={null}
                setActiveSection={() => {}}
                isDarkMode={isDarkMode}
                setIsDarkMode={setIsDarkMode}
                calculatorVersion={calculatorVersion}
                setCalculatorVersion={setCalculatorVersion}
                sectionCompletionStatus={sectionCompletionStatus}
                noWrapperStyling={true}
              >
                <AdminFeedbackPage />
              </DashboardLayout>
            </Suspense>
          </AdminRoute>
        } />

        <Route path="/admin/deletions" element={
          <AdminRoute>
            <Suspense fallback={<div className="loading-screen">Loading...</div>}>
              <DashboardLayout
                isProCalculator={false}
                sections={sections}
                activeSection={null}
                setActiveSection={() => {}}
                isDarkMode={isDarkMode}
                setIsDarkMode={setIsDarkMode}
                calculatorVersion={calculatorVersion}
                setCalculatorVersion={setCalculatorVersion}
                sectionCompletionStatus={sectionCompletionStatus}
                noWrapperStyling={true}
              >
                <AdminDeletionRequestsPage />
              </DashboardLayout>
            </Suspense>
          </AdminRoute>
        } />

        <Route path="/admin/recommended-products" element={
          <AdminRoute>
            <Suspense fallback={<div className="loading-screen">Loading...</div>}>
              <DashboardLayout
                isProCalculator={false}
                sections={sections}
                activeSection={null}
                setActiveSection={() => {}}
                isDarkMode={isDarkMode}
                setIsDarkMode={setIsDarkMode}
                calculatorVersion={calculatorVersion}
                setCalculatorVersion={setCalculatorVersion}
                sectionCompletionStatus={sectionCompletionStatus}
                noWrapperStyling={true}
              >
                <AdminRecommendedProductsPage />
              </DashboardLayout>
            </Suspense>
          </AdminRoute>
        } />

        <Route path="/admin/activity" element={
          <AdminRoute>
            <Suspense fallback={<div className="loading-screen">Loading...</div>}>
              <DashboardLayout
                isProCalculator={false}
                sections={sections}
                activeSection={null}
                setActiveSection={() => {}}
                isDarkMode={isDarkMode}
                setIsDarkMode={setIsDarkMode}
                calculatorVersion={calculatorVersion}
                setCalculatorVersion={setCalculatorVersion}
                sectionCompletionStatus={sectionCompletionStatus}
                noWrapperStyling={true}
              >
                <AdminActivityPage />
              </DashboardLayout>
            </Suspense>
          </AdminRoute>
        } />

        <Route path="/admin/settings" element={
          <AdminRoute>
            <Suspense fallback={<div className="loading-screen">Loading...</div>}>
              <DashboardLayout
                isProCalculator={false}
                sections={sections}
                activeSection={null}
                setActiveSection={() => {}}
                isDarkMode={isDarkMode}
                setIsDarkMode={setIsDarkMode}
                calculatorVersion={calculatorVersion}
                setCalculatorVersion={setCalculatorVersion}
                sectionCompletionStatus={sectionCompletionStatus}
                noWrapperStyling={true}
              >
                <AdminGlobalSettingsPage />
              </DashboardLayout>
            </Suspense>
          </AdminRoute>
        } />
        
        <Route path="/privacy-policy" element={
          <DashboardLayout
            isProCalculator={false}
            sections={sections}
            activeSection={null}
            setActiveSection={() => {}}
            isDarkMode={isDarkMode}
            setIsDarkMode={setIsDarkMode}
            calculatorVersion={calculatorVersion}
            setCalculatorVersion={setCalculatorVersion}
            sectionCompletionStatus={sectionCompletionStatus}
            noWrapperStyling={true}
          >
            <PrivacyPolicyPage />
          </DashboardLayout>
        } />
        <Route path="/terms-of-service" element={
          <DashboardLayout
            isProCalculator={false}
            sections={sections}
            activeSection={null}
            setActiveSection={() => {}}
            isDarkMode={isDarkMode}
            setIsDarkMode={setIsDarkMode}
            calculatorVersion={calculatorVersion}
            setCalculatorVersion={setCalculatorVersion}
            sectionCompletionStatus={sectionCompletionStatus}
            noWrapperStyling={true}
          >
            <TermsOfServicePage />
          </DashboardLayout>
        } />
        <Route path="/about-us" element={
          <DashboardLayout
            isProCalculator={false} // Not part of pro calculator flow
            sections={sections} // Pass sections for sidebar consistency
            activeSection={null} // No pro calc section active
            setActiveSection={() => {}} // No pro calc section to set
            isDarkMode={isDarkMode}
            setIsDarkMode={setIsDarkMode}
            calculatorVersion={calculatorVersion}
            setCalculatorVersion={setCalculatorVersion}
            sectionCompletionStatus={sectionCompletionStatus}
          >
            <AboutUsPage />
          </DashboardLayout>
        } />
        
        <Route path="*" element={<Navigate to="/" />} />
      </Routes>
    </div>
  );
}

function App() {
  return <AppContent />;
}

export default App;
